'use-client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { Controller, useFormContext } from 'react-hook-form';

export interface Options {
	value: string;
	label: React.ReactNode;
	renderLabel?: React.ReactNode;
}

interface SelectFormProps {
	name: string;
	options: Options[];
	label: string;
	placeholder?: string;
	className?: string;
	labelClassName?: string;
	sendAsNumber?: boolean;
	considerWatchWhenDisplayingError?: boolean;
	isSearchable?: boolean;
	isDisabled?: boolean;
	setSelectedItem?: (item: any) => void;
	noOptionsMessage?: string;
	errorMessage?: string;
	deps?: string;
}

export default function SelectForm({
	name,
	placeholder,
	options,
	label,
	className,
	labelClassName,
	sendAsNumber = true,
	considerWatchWhenDisplayingError = true,
	isDisabled = false,
	setSelectedItem,
	noOptionsMessage = 'No Options',
	errorMessage = undefined,
	deps = undefined
}: SelectFormProps) {
	const { control, watch } = useFormContext();
	return (
		<Controller
			control={control}
			name={name}
			{...(deps && { rules: { deps: deps } })}
			render={({ field: { value, onChange }, fieldState }) => {
				const doesValueExists = value !== null && value !== undefined;
				return (
					<div className="flex flex-col gap-1">
						<span className={`text-sm ${labelClassName}`}>{label}</span>
						<Select
							onValueChange={(value) => {
								onChange(sendAsNumber ? Number(value) : value);
								if (setSelectedItem) setSelectedItem(value);
							}}
							value={doesValueExists ? options.find((option) => option.value === value.toString())?.value : undefined}
							disabled={isDisabled}
						>
							<SelectTrigger
								className={`bg-white focus:ring-0 ${
									fieldState.error && (considerWatchWhenDisplayingError ? !watch(name) : true)
										? 'rounded border border-error-300'
										: ''
								} ${className}`}
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder={placeholder ?? 'Select...'} />
							</SelectTrigger>
							<SelectContent>
								{options.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
								{options.length === 0 && (
									<SelectItem value="no-options" disabled>
										{noOptionsMessage}
									</SelectItem>
								)}
							</SelectContent>
						</Select>

						{fieldState.error && (considerWatchWhenDisplayingError ? !watch(name) : true) ? (
							<span className="text-sm text-error-500">{errorMessage ?? `${label} is required`}</span>
						) : null}
					</div>
				);
			}}
		/>
	);
}
